import type { JSX } from 'react';
import { Users, UserPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ExistingCandidateTab } from './ExistingCandidateTab';
import { NewCandidateTab } from './NewCandidateTab';
import { useAddCandidateDialog } from '../../hooks/useAddCandidateDialog';
import type { AddCandidateDialogProps } from './AddCandidateDialog.types';

function DialogTabsList(): JSX.Element {
  return (
    <TabsList className="grid w-full grid-cols-2">
      <TabsTrigger value="existing" className="flex items-center gap-2">
        <Users className="h-4 w-4" />
        Existing
      </TabsTrigger>
      <TabsTrigger value="new" className="flex items-center gap-2">
        <UserPlus className="h-4 w-4" />
        Create New Candidate
      </TabsTrigger>
    </TabsList>
  );
}

export function AddCandidateDialog({
  open,
  onOpenChange,
  positionId,
  candidates,
  isCandidatesLoading = false,
  linkedCandidateIds,
}: AddCandidateDialogProps): JSX.Element {
  const d = useAddCandidateDialog({ positionId, onOpenChange });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-row-click-ignore>
        <DialogHeader>
          <DialogTitle>Add Candidate to Position</DialogTitle>
          <DialogDescription>Link an existing candidate or create a new one.</DialogDescription>
        </DialogHeader>
        <Tabs value={d.activeTab} onValueChange={(v) => d.setActiveTab(v as 'existing' | 'new')}>
          <DialogTabsList />
          <ExistingCandidateTab
            candidates={candidates}
            isCandidatesLoading={isCandidatesLoading}
            selectedCandidateId={d.selectedCandidateId}
            onSelect={d.setSelectedCandidateId}
            linkedCandidateIds={linkedCandidateIds}
            onLink={d.handleLinkExisting}
            onCancel={d.handleClose}
            isAdding={d.isAdding}
          />
          <NewCandidateTab
            register={d.form.register}
            errors={d.form.formState.errors}
            onSubmit={d.handleCreateAndLink}
            onCancel={d.handleClose}
            isAdding={d.isAdding}
            country={d.country}
            onCountryChange={d.setCountry}
          />
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
